import { useState } from "react";
import {
    FaInfoCircle, FaIndustry, FaUsers, FaChartBar, FaDollarSign, FaBoxOpen,
    FaMask, FaUserCog, FaPhone, FaPlusCircle, FaEye, FaPencilAlt, FaTrashAlt,
    FaChevronDown, FaChevronUp, FaGlobe, FaBuilding, FaUser
} from "react-icons/fa";
import MenusSidebarManager, { AVAILABLE_MENUS, getMenuLabel } from "@/components/MenusSidebarManager";

// Definición de módulos con sus relaciones y descripciones
export const MODULOS_CON_RELACIONES = [
    {
        id: "empresas",
        nombre: "Empresas",
        descripcion: "Gestión de empresas del sistema",
        afecta: [],
        icon: FaIndustry,
        color: "bg-blue-500"
    },
    {
        id: "clientes",
        nombre: "Clientes",
        descripcion: "Acceso a reportes y panel de inicio",
        afecta: ["inicio", "cdrs_calendario"],
        icon: FaUsers,
        color: "bg-cyan-500"
    },
    {
        id: "reportes",
        nombre: "Reportes",
        descripcion: "Acceso a reportes y panel de inicio",
        afecta: ["inicio", "cdrs_calendario"],
        icon: FaChartBar,
        color: "bg-green-500"
    },
    {
        id: "tarifas",
        nombre: "Tarifas",
        descripcion: "Gestión de tarifas en Listado",
        afecta: ["admon_listado"],
        icon: FaDollarSign,
        color: "bg-emerald-500"
    },
    {
        id: "productos",
        nombre: "Productos",
        descripcion: "Gestión de productos en Listado",
        afecta: ["admon_listado"],
        icon: FaBoxOpen,
        color: "bg-purple-500"
    },
    {
        id: "perfiles",
        nombre: "Perfiles",
        descripcion: "Gestión de perfiles en Listado",
        afecta: ["admon_listado"],
        icon: FaMask,
        color: "bg-pink-500"
    },
    {
        id: "usuarios",
        nombre: "Usuarios",
        descripcion: "Gestión de usuarios en Listado",
        afecta: ["admon_listado"],
        icon: FaUserCog,
        color: "bg-indigo-500"
    },
    {
        id: "troncales",
        nombre: "Troncales",
        descripcion: "Gestión de troncales en Listado",
        afecta: ["admon_listado"],
        icon: FaPhone,
        color: "bg-orange-500"
    }
];

export const ALCANCES = [
    { value: "global", label: "Global", icon: <FaGlobe />, color: "text-purple-600", desc: "Acceso a todo el sistema" },
    { value: "empresa", label: "Empresa", icon: <FaBuilding />, color: "text-blue-600", desc: "Limitado a una empresa" },
    { value: "cliente", label: "Cliente", icon: <FaUser />, color: "text-green-600", desc: "Limitado a un cliente" }
];

/**
 * Componente reutilizable para formulario de perfil
 * Usado en GestionPerfiles y WizardUsuario
 * 
 * @param {Object} props
 * @param {Object} props.formData - Datos del formulario { nombre, configuracion }
 * @param {Function} props.onChange - Callback cuando cambian los datos
 * @param {boolean} props.showNombreField - Si debe mostrar el campo nombre (default: true)
 * @param {boolean} props.showJsonPreview - Si debe mostrar vista previa JSON
 * @param {Function} props.onJsonPreviewToggle - Callback para toggle de JSON preview
 */
export default function FormularioPerfil({
    formData,
    onChange,
    showNombreField = true,
    showJsonPreview = false,
    onJsonPreviewToggle = null
}) {
    const [jsonPreviewOpen, setJsonPreviewOpen] = useState(showJsonPreview);

    const toggleModulo = (modulo) => {
        const modulosActuales = formData.configuracion.modulosVisibles || [];
        const nuevosModulos = modulosActuales.includes(modulo)
            ? modulosActuales.filter(m => m !== modulo)
            : [...modulosActuales, modulo];

        let nuevosPermisos = { ...formData.configuracion.permisos };
        if (!nuevosModulos.includes(modulo)) {
            delete nuevosPermisos[modulo];
        } else if (!nuevosPermisos[modulo]) {
            nuevosPermisos[modulo] = { create: false, read: true, update: false, delete: false };
        }

        onChange({
            ...formData,
            configuracion: {
                ...formData.configuracion,
                modulosVisibles: nuevosModulos,
                permisos: nuevosPermisos
            }
        });
    };

    const togglePermiso = (modulo, permiso) => {
        onChange({
            ...formData,
            configuracion: {
                ...formData.configuracion,
                permisos: {
                    ...formData.configuracion.permisos,
                    [modulo]: {
                        ...formData.configuracion.permisos[modulo],
                        [permiso]: !formData.configuracion.permisos[modulo]?.[permiso]
                    }
                }
            }
        });
    };

    const handleToggleMenu = (menuId) => {
        const menusActuales = formData.configuracion.menus_sidebar || [];
        const nuevosMenus = menusActuales.includes(menuId)
            ? menusActuales.filter(m => m !== menuId)
            : [...menusActuales, menuId];

        onChange({
            ...formData,
            configuracion: {
                ...formData.configuracion,
                menus_sidebar: nuevosMenus
            }
        });
    };

    const handleToggleAllMenus = (parentId) => {
        const children = AVAILABLE_MENUS.filter(m => m.parent === parentId);
        const menusActuales = formData.configuracion.menus_sidebar || [];
        const allActive = children.every(c => menusActuales.includes(c.id));

        let nuevosMenus = [...menusActuales];
        children.forEach(child => {
            if (allActive) {
                nuevosMenus = nuevosMenus.filter(m => m !== child.id);
            } else {
                if (!nuevosMenus.includes(child.id)) {
                    nuevosMenus.push(child.id);
                }
            }
        });

        onChange({
            ...formData,
            configuracion: {
                ...formData.configuracion,
                menus_sidebar: nuevosMenus
            }
        });
    };

    const handleVerTodoChange = (checked) => {
        if (checked) {
            const todosLosPermisos = {};
            MODULOS_CON_RELACIONES.forEach(modulo => {
                todosLosPermisos[modulo.id] = {
                    create: true,
                    read: true,
                    update: true,
                    delete: true
                };
            });

            const todosLosMenus = AVAILABLE_MENUS.filter(m => m.parent !== null || m.id === 'inicio').map(menu => menu.id);

            onChange({
                ...formData,
                configuracion: {
                    ...formData.configuracion,
                    ver_todo: true,
                    modulosVisibles: MODULOS_CON_RELACIONES.map(m => m.id),
                    permisos: todosLosPermisos,
                    menus_sidebar: todosLosMenus
                }
            });
        } else {
            onChange({
                ...formData,
                configuracion: {
                    ...formData.configuracion,
                    ver_todo: false
                }
            });
        }
    };

    const handleJsonPreviewToggle = () => {
        const newState = !jsonPreviewOpen;
        setJsonPreviewOpen(newState);
        if (onJsonPreviewToggle) {
            onJsonPreviewToggle(newState);
        }
    };

    return (
        <div className="space-y-6">
            {/* Nombre del perfil */}
            {showNombreField && (
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre del Perfil *
                    </label>
                    <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => onChange({ ...formData, nombre: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        placeholder="Ej: Admin Empresa"
                    />
                </div>
            )}

            {/* Configuraciones básicas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.configuracion.ver_todo || false}
                            onChange={(e) => handleVerTodoChange(e.target.checked)}
                            className="w-5 h-5 text-purple-600 rounded"
                        />
                        <div>
                            <div className="font-semibold text-purple-900">Ver Todo</div>
                            <div className="text-sm text-purple-700">Acceso completo a todos los datos</div>
                        </div>
                    </label>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <label className="block text-sm font-semibold text-blue-900 mb-2">
                        Alcance del Perfil
                    </label>
                    <select
                        value={formData.configuracion.alcance || "cliente"}
                        onChange={(e) => onChange({
                            ...formData,
                            configuracion: {
                                ...formData.configuracion,
                                alcance: e.target.value
                            }
                        })}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                    >
                        {ALCANCES.map(a => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Módulos y Permisos con Árbol */}
            <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span>📦</span> Módulos y Permisos
                </h3>
                <div className="space-y-3">
                    {MODULOS_CON_RELACIONES.map(modulo => {
                        const moduloActivo = (formData.configuracion.modulosVisibles || []).includes(modulo.id);
                        const permisos = formData.configuracion.permisos?.[modulo.id] || {};
                        const IconoModulo = modulo.icon;

                        return (
                            <div
                                key={modulo.id}
                                className={`border-2 rounded-lg transition-all ${moduloActivo
                                    ? 'border-cyan-400 bg-cyan-50'
                                    : 'border-gray-200 bg-white'
                                    }`}
                            >
                                <div className="p-4">
                                    {/* Header del módulo */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <label className="flex items-center gap-3 cursor-pointer mb-2">
                                                <input
                                                    type="checkbox"
                                                    checked={moduloActivo}
                                                    onChange={() => toggleModulo(modulo.id)}
                                                    className="w-5 h-5 text-cyan-600 rounded accent-cyan-600"
                                                />
                                                {/* Círculo con icono */}
                                                <div className={`w-10 h-10 rounded-full ${modulo.color} flex items-center justify-center text-white shadow-md`}>
                                                    <IconoModulo size={20} />
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-gray-900">{modulo.nombre}</span>
                                                    <p className="text-xs text-gray-600">{modulo.descripcion}</p>
                                                </div>
                                            </label>

                                            {/* Árbol de dependencias */}
                                            {modulo.afecta.length > 0 && (
                                                <div className="ml-16 mt-2 pl-4 border-l-2 border-cyan-300">
                                                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                                                        <FaInfoCircle className="text-cyan-600" />
                                                        <span className="font-medium">Da acceso a:</span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {modulo.afecta.map(menuId => (
                                                            <span
                                                                key={menuId}
                                                                className="inline-flex items-center gap-1 bg-white border border-cyan-200 text-cyan-700 px-2 py-1 rounded text-xs"
                                                            >
                                                                <span>→</span>
                                                                {getMenuLabel(menuId)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Permisos CRUD con círculos */}
                                    {moduloActivo && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-16">
                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${permisos.create ? 'bg-green-500' : 'bg-gray-200'}`}>
                                                    <FaPlusCircle className={permisos.create ? 'text-white' : 'text-gray-400'} size={16} />
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={permisos.create || false}
                                                    onChange={() => togglePermiso(modulo.id, 'create')}
                                                    className="sr-only"
                                                />
                                                <span>Crear</span>
                                            </label>
                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${permisos.read ? 'bg-blue-500' : 'bg-gray-200'}`}>
                                                    <FaEye className={permisos.read ? 'text-white' : 'text-gray-400'} size={16} />
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={permisos.read || false}
                                                    onChange={() => togglePermiso(modulo.id, 'read')}
                                                    className="sr-only"
                                                />
                                                <span>Leer</span>
                                            </label>
                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${permisos.update ? 'bg-orange-500' : 'bg-gray-200'}`}>
                                                    <FaPencilAlt className={permisos.update ? 'text-white' : 'text-gray-400'} size={16} />
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={permisos.update || false}
                                                    onChange={() => togglePermiso(modulo.id, 'update')}
                                                    className="sr-only"
                                                />
                                                <span>Editar</span>
                                            </label>
                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${permisos.delete ? 'bg-red-500' : 'bg-gray-200'}`}>
                                                    <FaTrashAlt className={permisos.delete ? 'text-white' : 'text-gray-400'} size={16} />
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={permisos.delete || false}
                                                    onChange={() => togglePermiso(modulo.id, 'delete')}
                                                    className="sr-only"
                                                />
                                                <span>Eliminar</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Menús del Sidebar usando el componente reutilizable */}
            <div>
                <MenusSidebarManager
                    menusActivos={formData.configuracion.menus_sidebar || []}
                    onToggleMenu={handleToggleMenu}
                    onToggleAllMenus={handleToggleAllMenus}
                    showTitle={true}
                />
            </div>

            {/* Vista previa JSON (opcional) */}
            {onJsonPreviewToggle && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                        onClick={handleJsonPreviewToggle}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <span className="text-xl">🔍</span>
                            <span className="font-semibold text-gray-700">Vista Previa JSON</span>
                        </div>
                        {jsonPreviewOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    {jsonPreviewOpen && (
                        <div className="p-4 bg-gray-900">
                            <pre className="text-green-400 text-xs overflow-x-auto">
                                {JSON.stringify(formData.configuracion, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}